import React, { useState } from 'react';
import { motion } from 'framer-motion';
import landscapeData from '../../content/landscape.json';
import AxisModal from './AxisModal';

interface Axis {
  id: string;
  title: string;
  accent: string;
  image?: string;
  bullets: string[];
  note?: string;
  modal?: {
    title: string;
    desc: string;
    flow: string;
  };
  links?: Array<{
    label: string;
    href: string;
  }>;
}

const LandscapeAxesGrid: React.FC = () => {
  const [modalData, setModalData] = useState<{ title: string; desc: string; flow: string; links?: Array<{ label: string; href: string }> } | null>(null);
  const { axes } = landscapeData as { axes: Axis[] };

  const handleCardClick = (axis: Axis) => {
    // If axis has modal data, open modal
    if (axis.modal) {
      setModalData({
        ...axis.modal,
        links: axis.links // Pass links to modal
      });
    }
  };

  const closeModal = () => setModalData(null);

  return (
    <>
      <section className="py-6">
        <div className="container mx-auto px-4">
          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {axes.map((axis, index) => (
              <div
                key={axis.id}
                id={axis.id}
                onClick={() => handleCardClick(axis)}
                className={`
                  relative rounded-xl overflow-hidden shadow-lg hover:shadow-2xl
                  transition-all duration-300 transform hover:-translate-y-2
                  ${axis.modal ? 'cursor-pointer' : 'cursor-default'}
                `}
                role={axis.modal ? 'button' : 'article'}
                tabIndex={axis.modal ? 0 : undefined}
                aria-label={axis.title}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && axis.modal) {
                    e.preventDefault();
                    handleCardClick(axis);
                  }
                }}
              >
                {/* Image or Gradient Header */}
                <div className="h-32 flex items-center justify-center relative overflow-hidden">
                  {axis.image ? (
                    <>
                      <img
                        src={axis.image}
                        alt={axis.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40" />
                      <h3 className="relative text-xl md:text-2xl font-bold text-white text-center px-4 z-10" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)' }}>
                        {axis.title}
                      </h3>
                    </>
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center relative"
                      style={{
                        background: `linear-gradient(135deg, ${axis.accent.replace('from-[', '').replace('] to-[', ', ').replace(']', '')})`,
                        opacity: 0.85,
                        backdropFilter: 'blur(10px) saturate(150%)',
                        WebkitBackdropFilter: 'blur(10px) saturate(150%)'
                      }}
                    >
                      <h3 className="text-xl md:text-2xl font-bold text-black text-center px-4 relative z-10" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)' }}>
                        {axis.title}
                      </h3>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div
                  className="p-6 flex flex-col"
                  style={{
                    /* Verre dépoli grisâtre */
                    background: 'rgba(128, 128, 128, 0.12)',
                    backdropFilter: 'blur(10px) saturate(150%)',
                    WebkitBackdropFilter: 'blur(10px) saturate(150%)',
                    borderTop: '1px solid rgba(200, 200, 200, 0.3)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 2px 10px rgba(255, 255, 255, 0.2)',
                    willChange: 'backdrop-filter'
                  }}
                >
                  {/* Bullets */}
                  <ul className="space-y-2 mb-4 flex-1">
                    {axis.bullets.map((bullet, i) => (
                      <li key={i} className="text-base text-black leading-relaxed flex items-start font-medium drop-shadow-sm">
                        <span className="mr-2 text-black">•</span>
                        <span dangerouslySetInnerHTML={{ __html: bullet.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 hover:opacity-100 transition-opacity pointer-events-none" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      <AxisModal isOpen={!!modalData} onClose={closeModal} data={modalData} />
    </>
  );
};

export default LandscapeAxesGrid;
